import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import type { Nil } from '@pbt/pbt-ui-components'

import type { PreviousMedicalRecord } from '~/api/graphql/generated/types'
import { ClinicBrief } from '~/components/dashboard/timetable/scheduler/appointment/ClinicBrief'
import Typography from '~/components/elements/Typography/Typography'
import { ConsentStatusName } from '~/constants/previousMedicalRecord'
import { useConsentTexts, useGetSimplePmrMessage } from '~/utils/pmrUtils'

import styles from './TimelinePreviousMedicalRecords.module.scss'

interface TimelinePreviousMedicalRecordsProps {
  previousMedicalRecord?: PreviousMedicalRecord | Nil
}

const TimelinePreviousMedicalRecords: FC<
  TimelinePreviousMedicalRecordsProps
> = ({ previousMedicalRecord }) => {
  const { t } = useTranslation('Common')
  const contacts = previousMedicalRecord?.contacts ?? []

  const simpleMessage = useGetSimplePmrMessage(
    previousMedicalRecord?.priorVetCareStatus?.id,
  )
  const consentTexts = useConsentTexts(
    previousMedicalRecord?.consentStatus?.id,
    t('PMR_CONSENT.NO_PREVIOUS_PRACTICE_ADDED'),
  )

  if (previousMedicalRecord === null) {
    return null
  }

  if (simpleMessage) {
    return (
      <Grid container className={styles.root}>
        <Grid container direction="column" m={2} width="auto">
          <Grid item mb={1}>
            <Typography.H3 className={styles.titleText}>
              {t('PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
            </Typography.H3>
          </Grid>
          <Grid item>
            <Typography.Paragraph>{simpleMessage}</Typography.Paragraph>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  // Yes - Consent plus Previous clinic OR No - Did not consent to PMR
  // Both require priorVetCareStatus === YES and show clinics
  const { consentText, descriptionText, showConsentLine } = consentTexts
  const consentEntity = previousMedicalRecord?.consentStatus
  const showClinics = showConsentLine && contacts.length > 0

  return (
    <Grid container className={styles.root}>
      <Grid container direction="column" m={2} width="auto">
        <Grid item mb={1}>
          <Typography.H3 className={styles.titleText}>
            {t('PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
          </Typography.H3>
        </Grid>
        <Grid container item gap={1}>
          <Grid item>
            {showConsentLine && (
              <Typography.H3
                className={
                  consentEntity?.name === ConsentStatusName.DENIED
                    ? styles.consentTextError
                    : styles.consentText
                }
              >
                {consentText}
              </Typography.H3>
            )}
            <Typography.Paragraph>{descriptionText}</Typography.Paragraph>
          </Grid>
          {showClinics && (
            <Grid
              container
              item
              columnSpacing={4}
              direction="row"
              rowSpacing={2}
            >
              {contacts.map((contact) => (
                <Grid item key={contact.id}>
                  <ClinicBrief contact={contact} />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default TimelinePreviousMedicalRecords
